import { useCallback, useEffect } from 'react';
import { PDF_MAX_SCALE, PDF_MIN_SCALE, PDF_SCALE_STEP } from '@/constants/pdf';
import type { PdfDisplayMode } from './types';
import type { PdfViewerState } from './usePdfViewerState';

interface UsePdfNavigationArgs {
  state: PdfViewerState;
  displayMode: PdfDisplayMode;
  scrollToPage: (page: number, behavior?: ScrollBehavior, block?: 'start' | 'center') => void;
}

/** Anything that would swallow an N or a P the user meant as a character. */
const TYPING_TARGET_SELECTOR =
  'input,textarea,[contenteditable]:not([contenteditable="false"]),' +
  '[role="combobox"],[role="menu"],[role="listbox"]';

export function usePdfNavigation({ state, displayMode, scrollToPage }: UsePdfNavigationArgs) {
  const {
    currentPage, totalPages, searchResults, currentResultIndex,
    setCurrentPage, setScale, setCurrentResultIndex,
  } = state;

  const goToPage = useCallback((nextPage: number) => {
    setCurrentPage(nextPage);
    if (displayMode === 'scroll') {
      scrollToPage(nextPage, 'smooth', 'start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable
  }, [displayMode, scrollToPage]);

  const goToResult = useCallback((index: number) => {
    setCurrentResultIndex(index);
    const targetPage = searchResults[index].page;
    setCurrentPage(targetPage);
    if (displayMode === 'scroll') {
      scrollToPage(targetPage, 'smooth', 'center');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable
  }, [searchResults, displayMode, scrollToPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) goToPage(currentPage + 1);
  }, [currentPage, totalPages, goToPage]);

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + PDF_SCALE_STEP, PDF_MAX_SCALE));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - PDF_SCALE_STEP, PDF_MIN_SCALE));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable
  }, []);

  const handlePrevResult = useCallback(() => {
    if (searchResults.length === 0) return;
    goToResult(currentResultIndex > 0 ? currentResultIndex - 1 : searchResults.length - 1);
  }, [searchResults, currentResultIndex, goToResult]);

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    goToResult(currentResultIndex < searchResults.length - 1 ? currentResultIndex + 1 : 0);
  }, [searchResults, currentResultIndex, goToResult]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.(TYPING_TARGET_SELECTOR)) return;

      if (searchResults.length === 0) return;

      switch (e.key.toLowerCase()) {
        case 'n':
          handleNextResult();
          break;
        case 'p':
          handlePrevResult();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchResults.length, handleNextResult, handlePrevResult]);

  return {
    handlePrevPage,
    handleNextPage,
    handleZoomIn,
    handleZoomOut,
    handlePrevResult,
    handleNextResult,
    handleResultClick: goToResult,
  };
}
